// Costing method of the investment: "detailed" (the default, and every
// project saved before the choice existed) or "simplified" - three turnkey
// figures (PV €/kWp, battery €/kWh, other works €) and O&M as %/year.
// The backend reads the same keys (BESS_feasibility/Configuration/costing_mode.py);
// the detailed fields are kept, untouched, and apply again when the project
// returns to the detailed method.
import { Alert, Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

const EURO = "€";

export const SIMPLIFIED_COSTING_KEYS = [
  "simplePvEurPerKwp", "simpleBatteryEurPerKwh", "simpleOtherWorksEur",
  "simplePvOmPctYear", "simpleBatteryOmPctYear",
];

export const costingModeOf = (investmentCosts) =>
  String(investmentCosts?.costingMode || "").trim().toLowerCase() === "simplified" ? "simplified" : "detailed";

const num = (value) => {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};
const eur = (value) => `${Math.round(value).toLocaleString("en-US")} ${EURO}`;
const qty = (value) => value.toLocaleString("en-US", { maximumFractionDigits: 2 });

export function CostingMethodSelector({ mode, isGreek, onChange, SubSection }) {
  const tr = (el, en) => (isGreek ? el : en);
  const Wrapper = SubSection || (({ children }) => <Box>{children}</Box>);
  return (
    <Wrapper
      title={<span data-no-translate="">{tr("Τρόπος κοστολόγησης", "Costing method")}</span>}
      description={<span data-no-translate="">{mode === "simplified"
        ? tr("Τρία ποσά για πλήρως εγκατεστημένα συστήματα (Φ/Β ανά kWp, μπαταρία ανά kWh, λοιπά έργα) και συντήρηση ως %/έτος. Τα αναλυτικά πεδία κρατιούνται και ισχύουν ξανά αν επιστρέψετε στον αναλυτικό τρόπο.",
          "Three turnkey figures (PV per kWp, battery per kWh, other works) and O&M as %/year. The detailed fields are kept and apply again if you return to the detailed method.")
        : tr("Εξοπλισμός από τον κατάλογο, εγκατάσταση, λοιπός εξοπλισμός και έργα, απρόβλεπτα και συντήρηση ανά στοιχείο.",
          "Equipment from the catalogue, installation, balance of plant, contingency and O&M item by item.")}</span>}
    >
      <ToggleButtonGroup size="small" exclusive value={mode} data-no-translate=""
        onChange={(_, next) => next && next !== mode && onChange(next)}
        aria-label={tr("Τρόπος κοστολόγησης", "Costing method")}>
        <ToggleButton value="detailed" sx={{ textTransform: "none", px: 2 }}>
          {tr("Αναλυτικός", "Detailed")}
        </ToggleButton>
        <ToggleButton value="simplified" sx={{ textTransform: "none", px: 2 }}>
          {tr("Απλοποιημένος (ανά kWp / kWh)", "Simplified (per kWp / kWh)")}
        </ToggleButton>
      </ToggleButtonGroup>
    </Wrapper>
  );
}

export function SimplifiedCostingCard({
  isGreek, investmentCosts, updateInvestment, insurance, updateInsurance, landLease,
  showPv, showBattery, pvExisting, installedKwp, batteryKwh, batteryName, batteryOverride,
  LabeledField, SubSection, gridSx,
}) {
  const tr = (el, en) => (isGreek ? el : en);
  const pvRate = num(investmentCosts?.simplePvEurPerKwp);
  const batteryRate = num(investmentCosts?.simpleBatteryEurPerKwh);
  const otherWorks = num(investmentCosts?.simpleOtherWorksEur);
  const pvEur = showPv && installedKwp ? pvRate * installedKwp : null;
  const batteryEur = showBattery
    ? (batteryOverride != null ? batteryOverride : batteryKwh > 0 ? batteryRate * batteryKwh : null)
    : null;
  const known = (!showPv || pvExisting || pvEur != null) && (!showBattery || batteryEur != null);
  const total = (showPv && !pvExisting ? pvEur || 0 : 0) + (batteryEur || 0) + otherWorks;
  const field = (key, label, helperText, placeholder) => (
    <LabeledField key={key} id={{ simplePvEurPerKwp: "field-econ-simple-pv", simplePvOmPctYear: "field-econ-simple-pv-om" }[key]} label={label} type="number" fullWidth placeholder={placeholder}
      value={investmentCosts?.[key] ?? ""} onChange={updateInvestment(key)} helperText={helperText}
      data-testid={`simple-${key}`} />
  );
  const pvHelper = tr(
    "Πλαίσια, αντιστροφείς, εγκατάσταση, βάσεις, καλωδιώσεις, μελέτη, εργασία και απρόβλεπτα. Πολλαπλασιάζεται με την εγκατεστημένη ισχύ DC",
    "Modules, inverters, installation, mounting, cabling, design, labour and contingency. Multiplied by the installed DC capacity"
  ) + (installedKwp
    ? `: ${qty(installedKwp)} kWp${pvEur != null ? ` = ${eur(pvEur)}` : ""}.`
    : tr(" (ορίζεται από τον σχεδιασμό του Φ/Β κατά την εκτέλεση).", " (set by the PV design at run time)."))
    + (pvExisting ? tr(" Το Φ/Β είναι υφιστάμενο: δεν μπαίνει στο CAPEX· χρησιμοποιείται μόνο ως βάση της συντήρησης.",
      " The PV plant is existing: it is not in CAPEX and is used only as the basis of its O&M.") : "");
  const batteryHelper = tr(
    "Μπαταρία, PCS, εγκατάσταση και ενσωμάτωση. Πολλαπλασιάζεται με την εγκατεστημένη χωρητικότητα",
    "Battery, PCS, installation and integration. Multiplied by the installed capacity"
  ) + (batteryKwh > 0 ? `: ${qty(batteryKwh)} kWh${batteryOverride == null ? ` = ${eur(batteryRate * batteryKwh)}` : ""}.` : ".")
    + tr(" Στη σύγκριση μπαταριών κάθε μπαταρία τιμολογείται με τα δικά της kWh.",
      " In the battery comparison each battery is priced at its own kWh.");
  return (
    <SubSection
      title={<span data-no-translate="">{tr("Απλοποιημένη κοστολόγηση", "Simplified costing")}</span>}
      description={<span data-no-translate="">{tr("Τιμές για πλήρως εγκατεστημένα συστήματα, χωρίς ΦΠΑ. Η μελέτη δεν προσθέτει ΦΠΑ στο CAPEX και στο OPEX.",
        "Turnkey prices, without VAT. The study adds no VAT to CAPEX or OPEX.")}</span>}
    >
      <Box data-no-translate="" sx={{ display: "grid", gap: 2 }}>
        {batteryOverride != null && showBattery ? (
          <Alert severity="info">
            {tr(`Για την μπαταρία ${batteryName} έχει καταχωριστεί κόστος ${eur(batteryOverride)}, που χρησιμοποιείται αντί για €/kWh × kWh. Αλλάζει στον αναλυτικό τρόπο, στο «Κόστος εξοπλισμού» → «Επεξεργασία».`,
              `A cost of ${eur(batteryOverride)} is entered for the battery ${batteryName} and is used instead of €/kWh × kWh. Change it in the detailed method, under Equipment Cost → Edit.`)}
          </Alert>
        ) : null}
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
          {tr("Επένδυση", "Investment")}
        </Typography>
        <Box sx={gridSx}>
          {showPv ? field("simplePvEurPerKwp", tr(`Φ/Β, πλήρως εγκατεστημένο (${EURO}/kWp)`, `PV, turnkey (${EURO}/kWp)`), pvHelper, tr("π.χ. 650", "e.g. 650")) : null}
          {showBattery ? field("simpleBatteryEurPerKwh", tr(`Σύστημα μπαταρίας, πλήρως εγκατεστημένο (${EURO}/kWh)`, `Battery system, turnkey (${EURO}/kWh)`), batteryHelper, tr("π.χ. 300", "e.g. 300")) : null}
          {field("simpleOtherWorksEur", tr(`Λοιπά έργα (${EURO})`, `Other works (${EURO})`),
            tr("Σύνδεση με το δίκτυο, μετασχηματιστής, άδειες και ό,τι δεν περιλαμβάνεται παραπάνω, ως ένα ποσό για όλο το έργο.",
              "Grid connection, transformer, permits and anything not included above, as one amount for the whole project."),
            tr("π.χ. 25000", "e.g. 25000"))}
        </Box>
        <Typography variant="body2" data-testid="simple-costing-total" sx={{ color: "text.secondary" }}>
          {known
            ? tr(`Επένδυση πριν την επιχορήγηση: ${eur(total)}.`, `Investment before grant: ${eur(total)}.`)
            : tr("Η επένδυση υπολογίζεται κατά την εκτέλεση, όταν οριστεί η ισχύς του Φ/Β.",
              "The investment is computed at run time, once the PV capacity is set.")}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
          {tr("Ετήσιες λειτουργικές δαπάνες", "Annual operating costs")}
        </Typography>
        <Box sx={gridSx}>
          {showPv ? field("simplePvOmPctYear", tr("Συντήρηση Φ/Β (%/έτος)", "PV O&M (%/year)"),
            tr("Ετήσιο κόστος λειτουργίας και συντήρησης ως % του κόστους του Φ/Β (€/kWp × kWp).",
              "Annual operation and maintenance as a % of the PV cost (€/kWp × kWp)."), tr("π.χ. 1", "e.g. 1")) : null}
          {showBattery ? field("simpleBatteryOmPctYear", tr("Συντήρηση μπαταρίας (%/έτος)", "Battery O&M (%/year)"),
            tr("Ετήσιο κόστος λειτουργίας και συντήρησης ως % του κόστους του συστήματος μπαταρίας.",
              "Annual operation and maintenance as a % of the battery system cost."), tr("π.χ. 1.5", "e.g. 1.5")) : null}
          <LabeledField label={tr("Ασφάλιστρο (%/έτος)", "Insurance Rate (%/Year)")} type="number" fullWidth
            value={insurance ?? ""} onChange={updateInsurance} placeholder={tr("π.χ. 0.5", "e.g. 0.5")}
            helperText={tr("Ετήσιο ασφάλιστρο ως ποσοστό του επενδυτικού κόστους (CAPEX) του έργου.",
              "Annual insurance rate (% of the project's CAPEX).")} />
          <LabeledField label={tr(`Μίσθωση γης (${EURO}/έτος)`, `Land Leasing (${EURO}/Year)`)} type="number" fullWidth
            value={landLease ?? ""} onChange={updateInvestment("landLeasingEurYear")} placeholder={tr("π.χ. 15000", "e.g. 15000")}
            helperText={tr("Μίσθωση γης ανά έτος (μηδενίζεται εάν είναι ιδιοκτησία).", "Land lease per year (set to zero if owned).")} />
        </Box>
      </Box>
    </SubSection>
  );
}
